import React, { useState, useEffect } from "react";
import {
  Table, Card, Row, Col, Select, Button, Upload, message,
  Tag, Alert, Modal, Form, Input, Switch, Popconfirm,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { ErrorToast, SuccessToast } from "../../ToastComponent";

const { Option } = Select;

const ROLE_LABELS = { 1: "Credit", 2: "Central Operations", 3: "Operations" };
const ROLE_OPTIONS = [
  { value: 1, label: "Credit" },
  { value: 2, label: "Central Operations" },
  { value: 3, label: "Operations" },
];

const QUARTERS = [
  { value: 1, label: "Q1 (Apr - Jun)" },
  { value: 2, label: "Q2 (Jul - Sep)" },
  { value: 3, label: "Q3 (Oct - Dec)" },
  { value: 4, label: "Q4 (Jan - Mar)" },
];
const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

export const PerformanceSnapshotUpload = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState("");
  const [filters, setFilters] = useState({ quarter: 3, year: currentYear });

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [form] = Form.useForm();

  const token = JSON.parse(sessionStorage.getItem("user"))?.accessToken;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.quarter) params.append("quarter", filters.quarter);
      if (filters.year) params.append("year", filters.year);
      const res = await axios.get(
        `${Api_Url}api/v1/EmployeePerformanceSnapshot?${params.toString()}`,
        authHeader
      );
      setRows(res?.data?.data || []);
    } catch (e) {
      ErrorToast("Failed to fetch employee branch assignments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleUpload = async () => {
    if (!file) {
      message.warning("Please choose an Excel file first.");
      return;
    }
    setUploading(true);
    setReport("");
    try {
      const formData = new FormData();
      formData.append("ExcelFile", file);
      formData.append("Quarter", filters.quarter);
      formData.append("Year", filters.year);

      const res = await axios.post(
        `${Api_Url}api/v1/EmployeePerformanceSnapshot/upload-excel`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      SuccessToast(res?.data?.message || "Upload complete");
      setReport(res?.data?.data || "");
      setFile(null);
      fetchRows();
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Upload failed");
      if (e.response?.data?.data) setReport(e.response.data.data);
    } finally {
      setUploading(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEditModal = (record) => {
    setEditRecord(record);
    form.setFieldsValue({
      role: record.role,
      managerId: record.managerId ?? null,
      branchName: record.branchName || "",
      isPrimary: record.isPrimary || false,
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      setEditSaving(true);
      await axios.put(
        `${Api_Url}api/v1/EmployeePerformanceSnapshot/${editRecord.id}`,
        {
          id: editRecord.id,
          role: values.role,
          managerId: values.managerId || null,
          branchName: values.branchName,
          isPrimary: values.isPrimary,
        },
        authHeader
      );
      SuccessToast("Record updated successfully");
      setEditModalOpen(false);
      setEditRecord(null);
      fetchRows();
    } catch (e) {
      if (e?.errorFields) return; // form validation error — stay open
      ErrorToast(e.response?.data?.message || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (record) => {
    try {
      await axios.delete(
        `${Api_Url}api/v1/EmployeePerformanceSnapshot/${record.id}`,
        authHeader
      );
      SuccessToast("Record deleted successfully");
      fetchRows();
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Delete failed");
    }
  };

  // Download a CSV template with the expected columns
  const downloadTemplate = () => {
    const headers = [
      "Sr No",
      "Empl Id",
      "Emp Name",
      "Date of Joining",
      "Date of Exit",
      "Role",
      "Manager Empl Id",
      "Eligible Branch list",
    ];
    const sample = [
      "1", "EMP001", "(auto from profile)", "(auto)", "(auto)",
      "Credit", "MGR001", "Durg, Bilaspur",
    ];
    const csv =
      headers.join(",") + "\n" +
      sample.map((c) => (c.includes(",") ? `"${c}"` : c)).join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "EmployeeBranchAssignment_Template.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: "Emp Code",
      dataIndex: "employeeCode",
      sorter: (a, b) => (a.employeeCode || "").localeCompare(b.employeeCode || ""),
    },
    {
      title: "Employee",
      dataIndex: "employeeName",
      sorter: (a, b) => (a.employeeName || "").localeCompare(b.employeeName || ""),
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (r) => ROLE_LABELS[r] || "-",
      sorter: (a, b) => (a.role || 0) - (b.role || 0),
    },
    {
      title: "Manager",
      dataIndex: "managerName",
      render: (v) => v || "-",
    },
    {
      title: "Branch",
      dataIndex: "branchName",
      sorter: (a, b) => (a.branchName || "").localeCompare(b.branchName || ""),
    },
    {
      title: "Primary",
      dataIndex: "isPrimary",
      render: (v) => (v ? <Tag color="green">Primary</Tag> : <Tag>—</Tag>),
    },
    {
      title: "DOJ",
      dataIndex: "dateOfJoining",
      render: (d) => (d ? String(d).substring(0, 10) : "NA"),
    },
    {
      title: "DOE",
      dataIndex: "dateOfExit",
      render: (d) => (d ? String(d).substring(0, 10) : "NA"),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button type="primary" ghost onClick={() => openEditModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button danger ghost>Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <h3 className="mb-0">Employee Branch Assignment (Quarterly)</h3>
            <span className="text-muted">
              Assign each employee's Role, Manager and eligible branches for a quarter.
              Emp Name, DOJ and DOE are taken automatically from the employee master.
            </span>
          </div>
        </div>

        <Card className="mb-4">
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={8} md={5}>
              <label className="mb-1 d-block">Quarter</label>
              <Select
                value={filters.quarter}
                onChange={(v) => setFilters((p) => ({ ...p, quarter: v }))}
                style={{ width: "100%" }}
              >
                {QUARTERS.map((q) => (
                  <Option key={q.value} value={q.value}>{q.label}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={5}>
              <label className="mb-1 d-block">Year</label>
              <Select
                value={filters.year}
                onChange={(v) => setFilters((p) => ({ ...p, year: v }))}
                style={{ width: "100%" }}
              >
                {YEARS.map((y) => (
                  <Option key={y} value={y}>{y}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={24} md={14}>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Upload
                  beforeUpload={(f) => {
                    setFile(f);
                    return false; // prevent auto-upload
                  }}
                  maxCount={1}
                  fileList={file ? [file] : []}
                  onRemove={() => setFile(null)}
                  accept=".xlsx,.xls"
                >
                  <Button icon={<UploadOutlined />}>Choose Excel</Button>
                </Upload>
                <Button type="primary" loading={uploading} onClick={handleUpload}>
                  Upload for {QUARTERS.find((q) => q.value === filters.quarter)?.label?.split(" ")[0]} {filters.year}
                </Button>
                <Button onClick={downloadTemplate}>Download Template</Button>
              </div>
            </Col>
          </Row>
          <Alert
            className="mt-3"
            type="info"
            showIcon
            message="Re-uploading for the same employee + quarter replaces their previous rows. Branch names must match exactly and be comma-separated."
          />
        </Card>

        {report && (
          <Card className="mb-4" title="Upload Report" size="small">
            <pre style={{ whiteSpace: "pre-wrap", margin: 0, maxHeight: 260, overflow: "auto" }}>
              {report}
            </pre>
          </Card>
        )}

        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={rows}
            rowKey="id"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              defaultPageSize: 10,
              pageSizeOptions: ["10", "20", "50", "100", "200"],
            }}
            scroll={{ x: 1100 }}
          />
        </div>
      </div>

      {/* ── Edit Modal ─────────────────────────────────────────────────────── */}
      <Modal
        title={
          editRecord
            ? `Edit Assignment — ${editRecord.employeeName} (${editRecord.employeeCode})`
            : "Edit Assignment"
        }
        open={editModalOpen}
        onOk={handleEditSave}
        onCancel={() => {
          setEditModalOpen(false);
          setEditRecord(null);
          form.resetFields();
        }}
        okText="Save Changes"
        confirmLoading={editSaving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Please select a role" }]}
          >
            <Select placeholder="Select role">
              {ROLE_OPTIONS.map((r) => (
                <Option key={r.value} value={r.value}>{r.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="managerId"
            label="Manager ID"
          >
            <Input type="number" placeholder="Manager's employee ID (optional)" />
          </Form.Item>

          <Form.Item
            name="branchName"
            label="Branch Name"
            rules={[{ required: true, message: "Please enter a branch name" }]}
          >
            <Input placeholder="e.g. Durg, Bilaspur (comma-separated for multiple)" />
          </Form.Item>

          <Form.Item
            name="isPrimary"
            label="Primary Branch"
            valuePropName="checked"
          >
            <Switch checkedChildren="Primary" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
